import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchEndProducts } from './communityApi';
import EmailCapture from './EmailCapture';
import './App.css';

// A simpler, "shop-window" cousin of /hub. Pulls the same catalog, picks
// items most likely to inspire (mini-apps and games up top), no filters /
// search / sort knobs. /hub is the full browse experience.

const CATEGORY_ORDER = ['miniapp', 'game', 'image', 'audio', 'video', 'podcast'];
const FEATURED_LIMIT = 24;

function categoryOf(p) {
  return (p.contentCategory || p.type || p.productType || '').toLowerCase();
}

function thumbnailFor(p) {
  return p.thumbnailURL || p.thumbnail || p.coverImageURL || p.coverURL || '';
}

function productLink(p) {
  const shortUid = p.publisherShortUid;
  const slug = p.slug || p.packageId || p._id;
  if (shortUid && slug) return `/u/${shortUid}/${slug}/`;
  if (slug) return `/hub/${slug}`;
  return '/hub';
}

const GalleryPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const raw = await fetchEndProducts({});
        if (cancelled) return;
        const published = raw.filter((p) => !!(p.publisherShortUid || p.publisherUid));
        // Prioritise: visual-first categories first, then by recency.
        const sorted = published.slice().sort((a, b) => {
          const aCat = CATEGORY_ORDER.indexOf(categoryOf(a));
          const bCat = CATEGORY_ORDER.indexOf(categoryOf(b));
          const aBucket = aCat === -1 ? 99 : aCat;
          const bBucket = bCat === -1 ? 99 : bCat;
          if (aBucket !== bBucket) return aBucket - bBucket;
          const aT = a.updatedAt || a.publishedAt || a.createdAt || '';
          const bT = b.updatedAt || b.publishedAt || b.createdAt || '';
          return String(bT).localeCompare(String(aT));
        });
        setProducts(sorted.slice(0, FEATURED_LIMIT));
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mp-page" style={{ background: '#fff', minHeight: 'calc(100vh - 60px)' }}>
      <section style={{ maxWidth: 1120, margin: '0 auto', padding: '64px 24px 24px' }}>
        <span className="trust-badge trust-official" style={{ display: 'inline-block', marginBottom: 16 }}>
          Gallery
        </span>
        <h1 style={{ fontSize: '2.6rem', fontWeight: 800, color: '#111827', margin: '0 0 16px', lineHeight: 1.1 }}>
          Things people made in mChatAI
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 12px', maxWidth: 720 }}>
          Real creations from real users — mini-apps, games, podcasts, images, sites. Pick one to play with, or remix it as the starting point for your own.
        </p>
        <Link to="/hub" style={{ display: 'inline-block', color: '#3b82f6', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 600 }}>
          Browse the full Hub →
        </Link>
      </section>

      <section style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 24px 64px' }}>
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: 16, borderRadius: 8, marginBottom: 24
          }}>
            Could not load the gallery: {error}
          </div>
        )}

        {loading && (
          <p style={{ color: '#6b7280' }}>Loading…</p>
        )}

        {!loading && !error && products.length === 0 && (
          <div style={{
            background: '#f9fafb', border: '1px dashed #d1d5db',
            padding: 24, borderRadius: 12, color: '#6b7280', textAlign: 'center'
          }}>
            <p style={{ margin: 0 }}>
              No published creations yet. Be the first — open mChatAI+, build something, and hit "Publish to Hub."
            </p>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16
        }}>
          {products.map((p) => {
            const slug = p.slug || p.packageId || p._id;
            const link = productLink(p);
            const thumb = thumbnailFor(p);
            const cat = categoryOf(p);
            return (
              <Link
                key={slug}
                to={link}
                style={{
                  display: 'block',
                  textDecoration: 'none',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.15s, transform 0.15s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  aspectRatio: '16 / 10',
                  background: thumb ? `center / cover no-repeat url(${thumb})` : 'linear-gradient(135deg, #eff6ff 0%, #ede9fe 100%)',
                  position: 'relative'
                }}>
                  {cat && (
                    <span style={{
                      position: 'absolute', top: 8, left: 8,
                      background: 'rgba(17,24,39,0.7)', color: '#fff',
                      padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600,
                      textTransform: 'capitalize'
                    }}>
                      {cat}
                    </span>
                  )}
                </div>
                <div style={{ padding: 14 }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 4px', lineHeight: 1.25 }}>
                    {p.name || slug}
                  </h3>
                  {p.publisherName && (
                    <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 8px' }}>
                      by {p.publisherName}
                    </p>
                  )}
                  {p.summary && (
                    <p style={{ fontSize: '0.85rem', color: '#4b5563', margin: 0, lineHeight: 1.45 }}>
                      {p.summary.length > 110 ? p.summary.slice(0, 110) + '…' : p.summary}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px 96px' }}>
        <EmailCapture
          source="gallery-footer"
          title="Weekly community spotlight"
          subtitle="One short email each week: the most-remixed creations, plus the prompts behind them."
        />
      </section>
    </div>
  );
};

export default GalleryPage;
